import { safeJuice } from './safeJuice';

export type InlineCssMessage = { id: number; html: string };

type PendingRequest = { html: string; resolve: (html: string) => void };

const pending = new Map<number, PendingRequest>();
let nextId = 0;
let worker: Worker | null | undefined;

function handleWorkerError(): void {
  pending.forEach(({ html, resolve }) => resolve(safeJuice(html)));
  pending.clear();
  worker?.terminate();
  worker = null;
}

function getWorker(): Worker | null {
  if (worker !== undefined) return worker;

  try {
    worker = new Worker(new URL('./inlineCss.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.addEventListener(
      'message',
      ({ data }: MessageEvent<InlineCssMessage>) => {
        pending.get(data.id)?.resolve(data.html);
        pending.delete(data.id);
      },
    );
    worker.addEventListener('error', handleWorkerError);
  } catch {
    worker = null;
  }

  return worker;
}

/**
 * Converts CSS style tags to inline styles in a worker, or on the main thread
 * where no worker can be started.
 */
export function inlineCss(html: string): Promise<string> {
  const inlineCssWorker = getWorker();

  if (!inlineCssWorker) {
    return Promise.resolve(safeJuice(html));
  }

  return new Promise((resolve) => {
    const id = nextId++;
    pending.set(id, { html, resolve });
    inlineCssWorker.postMessage({ id, html } satisfies InlineCssMessage);
  });
}
